import { FC, useState, useMemo, useCallback, useEffect } from "react";
import { CreateTagWrapper, Title, SubmitButton, Label, PointResultContainer, CreateTagModalWrapper, CloseIconWrapper, SearchIconWrapper } from "./style";
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from "@brg/ui";
import { Input } from "@brg/input";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { createNewTag } from "shared/actions/tags";
import { API, Designer, DesignerAPI, Endpoints, Tag, TagsAPI } from "@brg/api";
import { TagsActions } from "shared/reducers/Tags";
import ResItem from "_geograffee/places/pages/tagPlaces/components/map/resultitem";
import { useDebouncedEffect } from "shared/components/DropdownSearchBar/DropdownSearchBar";
import ProjectAuthorsEdit from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { InfoIcon } from "shared/components/InfoIcon/InfoIcon";
import CloseIconSmall from "shared/icons/CloseIconSmall";
import SearchIcon16 from "shared/icons/SearchIcon16";
import { LayoutConst } from "core";
import { AppTester } from "supporting";
import { getTagsModalLocalNameLabelKey } from "shared/helpers/tagAdminLocalLang";

export const CreateTagComponent: FC<{ 
    onCreateTag?: (value?: any) => void, 
    onClose: () => void, 
    isModal?: boolean,
    isEducation?: boolean,
    editTag?: Tag<'Global'>,
    onUpdateTag?: (value?: any) => void
}> = ({ 
        onClose, 
        isModal,
        isEducation = false,
        onCreateTag,
        editTag,
        onUpdateTag
    }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const {info, error } = useToasts();
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isDafes = AppTester.isGeoDafes() || AppTester?.isApp('kz')
    const { currentPlatform } = useSelector(TagAdminSelector)
    const [pointResults, setPoinResults] = useState<any[] | null>([])
    const [data, setData] = useState({
        nameEng: '',
        nameRu: '',
        user: '',
        coordinate: '',
        author: null
    })

    const isEditMode = useMemo(() => !!editTag, [editTag])

    const disable = useMemo(() => (data?.nameEng === '' || data?.nameRu === '') , [data])

    const parent = useMemo(
        () => currentPlatform?.editCategory?.mainTag || undefined , [currentPlatform])
    
    useEffect(() => {
        if (isEditMode && editTag) {
            const nameEng = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'en')?.name || '';
            const nameRu = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'ru')?.name || '';
            setData({
                nameEng,
                nameRu,
                user: '',
                coordinate: editTag.point ? `${editTag.point.latitude} ${editTag.point.longitude}` : '',
                author: null
            });
        }
    }, [isEditMode, editTag])

    const onClickRes = (coor: string) => {
        setData({...data, ['coordinate']: coor })
        setPoinResults(null)
    }

    // @ts-ignore
    const onProjectAuthorsChange = useCallback(({ authors }) => {
        if (authors.length > 1) {
            error('Автор может быть только один!');
            return;
        }

        if (authors.length) {
            const [author] = authors;
            DesignerAPI.Get(({
                idDesigner: author.accountId
            }))
            .then((v) => v.json())
            .then((v) => API.toLang<Designer>(v, i18n.language))
            .then(
                (v) => {
                    //@ts-ignore
                    setData({...data, ['author']: [v]});
            })
        }
    }, [setData, error, i18n]);

    const onSearchPoint = () => {
        fetch(`https://geocode-maps.yandex.ru/1.x/?apikey=44fd3270-0c0b-444d-a600-6f3b5e5a6343&geocode=${data?.coordinate.split(" ").reverse().join(" ")}&format=json`)
        .then((response) => {
            return response.json();
        })
        .then((data) => {
            if (data.error) return;

            const coll = data.response.GeoObjectCollection.featureMember;

            if (coll.length > 0) {
                //@ts-ignore
                const collSorted = coll.map((item, i) => {
                    return  <ResItem
                                key={i}
                                item={item}
                                className={'point-item'}
                                isNoResult={false}
                                name={item.GeoObject.name}
                                description={item.GeoObject.description}
                                coordinates={item.GeoObject.Point.pos}
                                styles={{
                                    background: '#f2f2f2',
                                    color: '#151514 !important',
                                }}
                                onClickRes={() => {
                                    onClickRes(item.GeoObject.Point.pos.split(" ").reverse().join(" "))
                                }}
                            />
                })
                setPoinResults(collSorted);
                return;
            }
            setPoinResults(null)
            return;
        });
    }

    useDebouncedEffect(() => {

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?:,.\d+(?:[,.]\d+)?)*$/)) {
            setData({...data, ['coordinate']: data?.coordinate.replace(", ", " ")})
        }

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            //out of range check
            let latitude = data?.coordinate.split(" ")[0]
            let longitude = data?.coordinate.split(" ")[1]
            //@ts-ignore
            if (Math.abs(latitude) > 90) {
                //@ts-ignore
                if (Math.abs(longitude) > 90 || Math.abs(latitude) > 180) return
                setData({...data, ['coordinate']: [longitude, latitude].join(" ")})
                return
            }

            //@ts-ignore
            if (Math.abs(longitude) > 180) return

            setData({...data, ['coordinate']: data?.coordinate})
        }

        if (data?.coordinate.length > 3 && !data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            onSearchPoint();
        }

        if (data?.coordinate.length === 0) {
            return
        }

    }, [data?.coordinate], 1000);

    const onUpdateUserTags = async (newTagId: string) => {
        //@ts-ignore
        if (!data?.author?.length) return null;
        const designer = (data?.author?.[0] as unknown) as Designer<'Local'>;

        return  await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/UpdateTagList')
        .post()
        .addQuery('tagsIds', [...designer?.tagListId, newTagId])
        .addQuery('designerId', designer?.designerId)
        .send()
        .then((res) => {
          if (res.status === 200) {
            info('Теги пользователя обновлены');
          } else {
            error('Ошибка запроса');
          }
        })
    }

    const onSubmit = () => {
        if (!isEditMode && !parent) {
            return;
        }

        if (data?.nameRu.trim().length < 2 || data?.nameEng.trim().length < 2) {
            error(t('main_admin_tag.error.min_length')); 
            return;
        }

        if (isEditMode && editTag) {
            // Обновление существующего тега
            const updatedTag = {
                ...editTag,
                name: [
                  { lang: 'En', name: data?.nameEng },
                  { lang: 'Ru', name: data?.nameRu },
                ],
                ...(!!data?.coordinate?.length && {
                    point: {
                        name: [
                          { lang: 'En', name: data?.nameEng },
                          { lang: 'Ru', name: data?.nameRu },
                        ],
                        latitude: parseFloat(data?.coordinate.split(" ")[0]),
                        longitude: parseFloat(data?.coordinate.split(" ")[1]),
                      },
                })
            };

            TagsAPI.UpdateTag(updatedTag)
                .then((res) => {
                    if (res?.status === 200) {
                        onUpdateTag && onUpdateTag(updatedTag);
                        onClose && onClose();
                    } else if (res?.status === 400) {
                        error(t('tags_modal.update_tag_name_exists'));
                    } else {
                        error(t('tags_modal.update_tag_error'));
                    }
                })
                .catch((err: any) => {
                    error(t('tags_modal.update_tag_error'));
                });
            return;
        }

        // Создание нового тега
        const newTagProps = {
            name: [
              { lang: 'En', name: data?.nameEng },
              { lang: 'Ru', name: data?.nameRu },
            ],
            groupId: parent?.tagId,
            groupIds: [parent?.tagId],
            entityTypes: parent.entityTypes,
            level: 0,
            isEducationTag: isEducation,
            ...(!!data?.coordinate?.length && {
                point: {
                    name: [
                      { lang: 'En', name: data?.nameEng },
                      { lang: 'Ru', name: data?.nameRu },
                    ],
                    latitude: parseFloat(data?.coordinate.split(" ")[0]),
                    longitude: parseFloat(data?.coordinate.split(" ")[1]),
                  },
            })

        }

        API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/TagAdmin/AddTags')
        .post()
        .body([newTagProps])
        .send()
        .then(res => {
            if (res) {
                const translatedTag = API.toLang(res, i18n.language)
                onUpdateUserTags(res?.tagId)
                dispatch(TagsActions.setNewTag(translatedTag))
                onClose && onClose()

                if (isModal && !!onCreateTag) {
                    onCreateTag && onCreateTag(res)
                } else {
                    info(t('add_new_tag_success'))
                    onCreateTag && onCreateTag();
                }
            }
        }).catch((err: any) => {
            error(t('add_new_tag_error'))
        })
    }

    const onInputChange = (ev: any, field: string) => {
        const value = ev.target.value;
        const baseRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F]*$/;
        const extendedRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F\u4e00-\u9fff]*$/;

        const regex = isDafes ? extendedRegex : baseRegex;
      
        if (!regex.test(value)) {
          return;
        }
      
        setData(prev => ({
          ...prev,
          [field]: value
        }));
    }

    const resetField = (fieldName: string) => {
        const targetObj = { target: { value: ''}}
        onInputChange(targetObj, fieldName)
    }

    if (isModal) {

        return (
           <CreateTagModalWrapper>
            <FlexBox justify="space-between" justifyMobile="space-between" offsetBottom={40}>
                <Title>{isEditMode ? t("tags_modal.edit_tag_title") : t("tags_modal.create_tag_title")}</Title>
                <BrgButton 
                    variant={ButtonVariant.Filled}
                    onClick={onSubmit} 
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </BrgButton>
            </FlexBox>
            <FlexBox direction="column" width={'100%'} gy={24}>
                <FlexBox gx={24} width={'100%'}>
                    <FlexBox style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameEng}
                            onChange={(ev: any) => onInputChange(ev, 'nameEng')}
                            orientation="column"
                            label={t("tags_modal.tag_name_eng")}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>    
                    <FlexBox  style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameRu}
                            onChange={(ev: any) => onInputChange(ev, 'nameRu')}
                            orientation="column"
                            label={t(getTagsModalLocalNameLabelKey())}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>
                </FlexBox>

                <FlexBox gx={24}  width={'100%'}>
                    <FlexBox style={{ position: 'relative' }} direction="column" width={'calc(50% - 12px)'} >
                        <CommonInput 
                            type={INPUT_TYPES.search}
                            value={data?.coordinate}
                            onChange={(ev: any) => onInputChange(ev, 'coordinate')}
                            orientation="column"
                            label={t("tags_modal.tag_geo_places")}
                            placeholder={t("tags_modal.geo_places_search")}
                            infoText={t('tags_modal.for_tag_point')}
                            onWhiteBG
                        />
                        {!!pointResults?.length && (
                            <PointResultContainer>
                                {pointResults}
                            </PointResultContainer>
                        )}
                    </FlexBox>
                    <FlexBox direction="column" width={'calc(50% - 12px)'}>
                        <ProjectAuthorsEdit
                            label={t("tags_modal.tag_user")}
                            infoText={t('tags_modal.for_tag_artists')}
                            value={data?.author || []}
                            onChange={onProjectAuthorsChange}
                            orientation="column"
                            persist
                        />
                    </FlexBox>
                </FlexBox>
            </FlexBox>
           </CreateTagModalWrapper> 
        )
    }

    return (
        <CreateTagWrapper>
            <FlexBox justify="space-between" justifyMobile="space-between" offsetBottom={40}>
                <Title>{isEditMode ? t("tags_modal.edit_tag_title") : t("tags_modal.create_tag_title")}</Title>
                <SubmitButton 
                    onClick={onSubmit} 
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </SubmitButton>
            </FlexBox>
            <FlexBox direction="column" width={'100%'} gy={24}>
                <FlexBox gx={24} width={'100%'}>
                    <FlexBox style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameEng}
                            onChange={(ev: any) => onInputChange(ev, 'nameEng')}
                            orientation="column"
                            label={t(`tags_modal.tag_name_eng`)}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>    
                    <FlexBox  style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameRu}
                            onChange={(ev: any) => onInputChange(ev, 'nameRu')}
                            orientation="column"
                            label={t(getTagsModalLocalNameLabelKey())}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>
                </FlexBox>

                <FlexBox gx={24}  width={'100%'}>
                    <FlexBox style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'} >
                        <CommonInput 
                            type={INPUT_TYPES.search}
                            value={data?.coordinate}
                            onChange={(ev: any) => onInputChange(ev, 'coordinate')}
                            orientation="column"
                            label={t("tags_modal.tag_geo_places")}
                            placeholder={t("tags_modal.geo_places_search")}
                            infoText={t('tags_modal.for_tag_point')}
                            onWhiteBG
                        />
                        {!!pointResults?.length && (
                            <PointResultContainer>
                                {pointResults}
                            </PointResultContainer>
                        )}

                    </FlexBox>
                    <FlexBox direction="column" width={'calc(50% - 12px)'}>
                        <ProjectAuthorsEdit
                            label={t("tags_modal.tag_user")}
                            infoText={t('tags_modal.for_tag_artists')}
                            value={data?.author || []}
                            onChange={onProjectAuthorsChange}
                            orientation={'column'}
                            persist
                        />
                    </FlexBox>
                </FlexBox>
            </FlexBox>
        </CreateTagWrapper>
    )
}import { FC, useState, useMemo, useCallback, useEffect } from "react";
import { CreateTagWrapper, Title, SubmitButton, Label, PointResultContainer, CreateTagModalWrapper, CloseIconWrapper, SearchIconWrapper } from "./style";
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from "@brg/ui";
import { Input } from "@brg/input";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TagAdminSelector } from "shared/reducers/tagAdminSlice";
import { createNewTag } from "shared/actions/tags";
import { API, Designer, DesignerAPI, Endpoints, Tag, TagsAPI } from "@brg/api";
import { TagsActions } from "shared/reducers/Tags";
import ResItem from "_geograffee/places/pages/tagPlaces/components/map/resultitem";
import { useDebouncedEffect } from "shared/components/DropdownSearchBar/DropdownSearchBar";
import ProjectAuthorsEdit from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { InfoIcon } from "shared/components/InfoIcon/InfoIcon";
import CloseIconSmall from "shared/icons/CloseIconSmall";
import SearchIcon16 from "shared/icons/SearchIcon16";
import { LayoutConst } from "core";
import { AppTester } from "supporting";
import { getTagsModalLocalNameLabelKey } from "shared/helpers/tagAdminLocalLang";

export const CreateTagComponent: FC<{ 
    onCreateTag?: (value?: any) => void, 
    onClose: () => void, 
    isModal?: boolean,
    isEducation?: boolean,
    editTag?: Tag<'Global'>,
    onUpdateTag?: (value?: any) => void
}> = ({ 
        onClose, 
        isModal,
        isEducation = false,
        onCreateTag,
        editTag,
        onUpdateTag
    }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const {info, error } = useToasts();
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const isDafes = AppTester.isGeoDafes() || AppTester?.isApp('kz')
    const { currentPlatform } = useSelector(TagAdminSelector)
    const [pointResults, setPoinResults] = useState<any[] | null>([])
    const [data, setData] = useState({
        nameEng: '',
        nameRu: '',
        user: '',
        coordinate: '',
        author: null
    })

    const isEditMode = useMemo(() => !!editTag, [editTag])

    const disable = useMemo(() => (data?.nameEng === '' || data?.nameRu === '') , [data])

    const parent = useMemo(
        () => currentPlatform?.editCategory?.mainTag || undefined , [currentPlatform])
    
    useEffect(() => {
        if (isEditMode && editTag) {
            const nameEng = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'en')?.name || '';
            const nameRu = editTag.name?.find((n: any) => n.lang?.toLowerCase() === 'ru')?.name || '';
            setData({
                nameEng,
                nameRu,
                user: '',
                coordinate: editTag.point ? `${editTag.point.latitude} ${editTag.point.longitude}` : '',
                author: null
            });
        }
    }, [isEditMode, editTag])

    const onClickRes = (coor: string) => {
        setData({...data, ['coordinate']: coor })
        setPoinResults(null)
    }

    // @ts-ignore
    const onProjectAuthorsChange = useCallback(({ authors }) => {
        if (authors.length > 1) {
            error('Автор может быть только один!');
            return;
        }

        if (authors.length) {
            const [author] = authors;
            DesignerAPI.Get(({
                idDesigner: author.accountId
            }))
            .then((v) => v.json())
            .then((v) => API.toLang<Designer>(v, i18n.language))
            .then(
                (v) => {
                    //@ts-ignore
                    setData({...data, ['author']: [v]});
            })
        }
    }, [setData, error, i18n]);

    const onSearchPoint = () => {
        fetch(`https://geocode-maps.yandex.ru/1.x/?apikey=44fd3270-0c0b-444d-a600-6f3b5e5a6343&geocode=${data?.coordinate.split(" ").reverse().join(" ")}&format=json`)
        .then((response) => {
            return response.json();
        })
        .then((data) => {
            if (data.error) return;

            const coll = data.response.GeoObjectCollection.featureMember;

            if (coll.length > 0) {
                //@ts-ignore
                const collSorted = coll.map((item, i) => {
                    return  <ResItem
                                key={i}
                                item={item}
                                className={'point-item'}
                                isNoResult={false}
                                name={item.GeoObject.name}
                                description={item.GeoObject.description}
                                coordinates={item.GeoObject.Point.pos}
                                styles={{
                                    background: '#f2f2f2',
                                    color: '#151514 !important',
                                }}
                                onClickRes={() => {
                                    onClickRes(item.GeoObject.Point.pos.split(" ").reverse().join(" "))
                                }}
                            />
                })
                setPoinResults(collSorted);
                return;
            }
            setPoinResults(null)
            return;
        });
    }

    useDebouncedEffect(() => {

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?:,.\d+(?:[,.]\d+)?)*$/)) {
            setData({...data, ['coordinate']: data?.coordinate.replace(", ", " ")})
        }

        if (data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            //out of range check
            let latitude = data?.coordinate.split(" ")[0]
            let longitude = data?.coordinate.split(" ")[1]
            //@ts-ignore
            if (Math.abs(latitude) > 90) {
                //@ts-ignore
                if (Math.abs(longitude) > 90 || Math.abs(latitude) > 180) return
                setData({...data, ['coordinate']: [longitude, latitude].join(" ")})
                return
            }

            //@ts-ignore
            if (Math.abs(longitude) > 180) return

            setData({...data, ['coordinate']: data?.coordinate})
        }

        if (data?.coordinate.length > 3 && !data?.coordinate.match(/^\d+(?:[,.]\d+)?(?: \d+(?:[,.]\d+)?)*$/)) {
            onSearchPoint();
        }

        if (data?.coordinate.length === 0) {
            return
        }

    }, [data?.coordinate], 1000);

    const onUpdateUserTags = async (newTagId: string) => {
        //@ts-ignore
        if (!data?.author?.length) return null;
        const designer = (data?.author?.[0] as unknown) as Designer<'Local'>;

        return  await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/UpdateTagList')
        .post()
        .addQuery('tagsIds', [...designer?.tagListId, newTagId])
        .addQuery('designerId', designer?.designerId)
        .send()
        .then((res) => {
          if (res.status === 200) {
            info('Теги пользователя обновлены');
          } else {
            error('Ошибка запроса');
          }
        })
    }

    const onSubmit = () => {
        if (!isEditMode && !parent) {
            return;
        }

        if (data?.nameRu.trim().length < 2 || data?.nameEng.trim().length < 2) {
            error(t('main_admin_tag.error.min_length')); 
            return;
        }

        if (isEditMode && editTag) {
            // Обновление существующего тега
            const updatedTag = {
                ...editTag,
                name: [
                  { lang: 'En', name: data?.nameEng },
                  { lang: 'Ru', name: data?.nameRu },
                ],
                ...(!!data?.coordinate?.length && {
                    point: {
                        name: [
                          { lang: 'En', name: data?.nameEng },
                          { lang: 'Ru', name: data?.nameRu },
                        ],
                        latitude: parseFloat(data?.coordinate.split(" ")[0]),
                        longitude: parseFloat(data?.coordinate.split(" ")[1]),
                      },
                })
            };

            TagsAPI.UpdateTag(updatedTag)
                .then((res) => {
                    if (res?.status === 200) {
                        onUpdateTag && onUpdateTag(updatedTag);
                        onClose && onClose();
                    } else if (res?.status === 400) {
                        error(t('tags_modal.update_tag_name_exists'));
                    } else {
                        error(t('tags_modal.update_tag_error'));
                    }
                })
                .catch((err: any) => {
                    error(t('tags_modal.update_tag_error'));
                });
            return;
        }

        // Создание нового тега
        const newTagProps = {
            name: [
              { lang: 'En', name: data?.nameEng },
              { lang: 'Ru', name: data?.nameRu },
            ],
            groupId: parent?.tagId,
            groupIds: [parent?.tagId],
            entityTypes: parent.entityTypes,
            level: 0,
            isEducationTag: isEducation,
            ...(!!data?.coordinate?.length && {
                point: {
                    name: [
                      { lang: 'En', name: data?.nameEng },
                      { lang: 'Ru', name: data?.nameRu },
                    ],
                    latitude: parseFloat(data?.coordinate.split(" ")[0]),
                    longitude: parseFloat(data?.coordinate.split(" ")[1]),
                  },
            })

        }

        API.request()
        .endpoint(Endpoints.tagsMnePw)
        .url('/api/TagAdmin/AddTags')
        .post()
        .body([newTagProps])
        .send()
        .then(res => {
            if (res) {
                const translatedTag = API.toLang(res, i18n.language)
                onUpdateUserTags(res?.tagId)
                dispatch(TagsActions.setNewTag(translatedTag))
                onClose && onClose()

                if (isModal && !!onCreateTag) {
                    onCreateTag && onCreateTag(res)
                } else {
                    info(t('add_new_tag_success'))
                    onCreateTag && onCreateTag();
                }
            }
        }).catch((err: any) => {
            error(t('add_new_tag_error'))
        })
    }

    const onInputChange = (ev: any, field: string) => {
        const value = ev.target.value;
        const baseRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F]*$/;
        const extendedRegex = /^[a-zA-Zа-яА-ЯёЁ0-9\s!"№%&,./:;?()+'\-"\u00C0-\u024F\u4e00-\u9fff]*$/;

        const regex = isDafes ? extendedRegex : baseRegex;
      
        if (!regex.test(value)) {
          return;
        }
      
        setData(prev => ({
          ...prev,
          [field]: value
        }));
    }

    const resetField = (fieldName: string) => {
        const targetObj = { target: { value: ''}}
        onInputChange(targetObj, fieldName)
    }

    if (isModal) {

        return (
           <CreateTagModalWrapper>
            <FlexBox justify="space-between" justifyMobile="space-between" offsetBottom={40}>
                <Title>{isEditMode ? t("tags_modal.edit_tag_title") : t("tags_modal.create_tag_title")}</Title>
                <BrgButton 
                    variant={ButtonVariant.Filled}
                    onClick={onSubmit} 
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </BrgButton>
            </FlexBox>
            <FlexBox direction="column" width={'100%'} gy={24}>
                <FlexBox gx={24} width={'100%'}>
                    <FlexBox style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameEng}
                            onChange={(ev: any) => onInputChange(ev, 'nameEng')}
                            orientation="column"
                            label={t("tags_modal.tag_name_eng")}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>    
                    <FlexBox  style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameRu}
                            onChange={(ev: any) => onInputChange(ev, 'nameRu')}
                            orientation="column"
                            label={t(getTagsModalLocalNameLabelKey())}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>
                </FlexBox>

                <FlexBox gx={24}  width={'100%'}>
                    <FlexBox style={{ position: 'relative' }} direction="column" width={'calc(50% - 12px)'} >
                        <CommonInput 
                            type={INPUT_TYPES.search}
                            value={data?.coordinate}
                            onChange={(ev: any) => onInputChange(ev, 'coordinate')}
                            orientation="column"
                            label={t("tags_modal.tag_geo_places")}
                            placeholder={t("tags_modal.geo_places_search")}
                            infoText={t('tags_modal.for_tag_point')}
                            onWhiteBG
                        />
                        {!!pointResults?.length && (
                            <PointResultContainer>
                                {pointResults}
                            </PointResultContainer>
                        )}
                    </FlexBox>
                    <FlexBox direction="column" width={'calc(50% - 12px)'}>
                        <ProjectAuthorsEdit
                            label={t("tags_modal.tag_user")}
                            infoText={t('tags_modal.for_tag_artists')}
                            value={data?.author || []}
                            onChange={onProjectAuthorsChange}
                            orientation="column"
                            persist
                        />
                    </FlexBox>
                </FlexBox>
            </FlexBox>
           </CreateTagModalWrapper> 
        )
    }

    return (
        <CreateTagWrapper>
            <FlexBox justify="space-between" justifyMobile="space-between" offsetBottom={40}>
                <Title>{isEditMode ? t("tags_modal.edit_tag_title") : t("tags_modal.create_tag_title")}</Title>
                <SubmitButton 
                    onClick={onSubmit} 
                    disabled={disable}
                >
                    {t("tags_modal.save_title")}
                </SubmitButton>
            </FlexBox>
            <FlexBox direction="column" width={'100%'} gy={24}>
                <FlexBox gx={24} width={'100%'}>
                    <FlexBox style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameEng}
                            onChange={(ev: any) => onInputChange(ev, 'nameEng')}
                            orientation="column"
                            label={t(`tags_modal.tag_name_eng`)}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>    
                    <FlexBox  style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'}>
                        <CommonInput 
                            type={INPUT_TYPES.text}
                            value={data?.nameRu}
                            onChange={(ev: any) => onInputChange(ev, 'nameRu')}
                            orientation="column"
                            label={t(getTagsModalLocalNameLabelKey())}
                            placeholder={t("tags_modal.tag_input_placeholder")}
                        />
                    </FlexBox>
                </FlexBox>

                <FlexBox gx={24}  width={'100%'}>
                    <FlexBox style={{ position: "relative" }} direction="column" width={'calc(50% - 12px)'} >
                        <CommonInput 
                            type={INPUT_TYPES.search}
                            value={data?.coordinate}
                            onChange={(ev: any) => onInputChange(ev, 'coordinate')}
                            orientation="column"
                            label={t("tags_modal.tag_geo_places")}
                            placeholder={t("tags_modal.geo_places_search")}
                            infoText={t('tags_modal.for_tag_point')}
                            onWhiteBG
                        />
                        {!!pointResults?.length && (
                            <PointResultContainer>
                                {pointResults}
                            </PointResultContainer>
                        )}

                    </FlexBox>
                    <FlexBox direction="column" width={'calc(50% - 12px)'}>
                        <ProjectAuthorsEdit
                            label={t("tags_modal.tag_user")}
                            infoText={t('tags_modal.for_tag_artists')}
                            value={data?.author || []}
                            onChange={onProjectAuthorsChange}
                            orientation={'column'}
                            persist
                        />
                    </FlexBox>
                </FlexBox>
            </FlexBox>
        </CreateTagWrapper>
    )
}